/**
 * Main-thread side of the worker protocol. Starts a run, relays progress, and
 * resolves with the ledger — which stays in this browser. The key is handed to
 * the worker once and is not kept here.
 */
import type {
  DoneMessage,
  ErrorKind,
  ProgressMessage,
  StageCompleteMessage,
  StartRequest,
  WorkerRequest,
  WorkerResponse,
} from './protocol.js'

export type RunInput = Omit<StartRequest, 'type' | 'run_id'>

export type RunCallbacks = {
  readonly onProgress?: (message: ProgressMessage) => void
  readonly onStageComplete?: (message: StageCompleteMessage) => void
}

export type RunHandle = {
  readonly run_id: string
  readonly done: Promise<DoneMessage>
  cancel(): void
}

export class WorkerRunError extends Error {
  override readonly name = 'WorkerRunError'
  readonly kind: ErrorKind
  constructor(kind: ErrorKind, message: string) {
    super(message)
    this.kind = kind
  }
}

export function startRun(input: RunInput, callbacks: RunCallbacks = {}): RunHandle {
  const worker = new Worker(new URL('./pipeline.worker.ts', import.meta.url), { type: 'module' })
  const run_id = crypto.randomUUID()
  const send = (message: WorkerRequest): void => worker.postMessage(message)

  const done = new Promise<DoneMessage>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data
      if (message.run_id !== run_id) return
      switch (message.type) {
        case 'progress':
          callbacks.onProgress?.(message)
          return
        case 'stage_complete':
          callbacks.onStageComplete?.(message)
          return
        case 'done':
          resolve(message)
          worker.terminate()
          return
        case 'error':
          reject(new WorkerRunError(message.kind, message.message))
          worker.terminate()
          return
      }
    }
    worker.onerror = () => {
      reject(new WorkerRunError('unknown', 'The pipeline worker failed to start.'))
      worker.terminate()
    }
  })

  send({ type: 'start', run_id, ...input })
  return { run_id, done, cancel: () => send({ type: 'cancel', run_id }) }
}
